import { toStringOrUndefined } from 'helpers'
import { CreateDocumentRequestV3, CreateDocumentRequestV3TypeEnum } from '../../../../api/models/create-document-request-v3'
import { UpdateDocumentRequestV3, UpdateDocumentRequestV3TypeEnum } from '../../../../api/models/update-document-request-v3'
import {
  CreateDocumentRequestWithFiles,
  DocumentCreate, DocumentUpdate,
  isPreviousDocumentCreate, isPreviousDocumentUpdate,
  isSupportingDocumentCreate, isSupportingDocumentUpdate,
  isTransportDocumentCreate, isTransportDocumentUpdate,
  PreviousDocumentCreate, PreviousDocumentUpdate, SupportingDocumentCreate, SupportingDocumentUpdate, TransportDocumentCreate,
  TransportDocumentUpdate, UpdateDocumentRequestWithFiles,
} from '../../document/models/request'

export function toCreateConsignmentItemSupportingDocumentRequest(
  document: SupportingDocumentCreate,
  transitOperationId: number,
  consignmentItemId: number | null,
): CreateDocumentRequestWithFiles {
  return {
    request: {
      complementOfInformation: document.complementOfInformation,
      documentLineItemNumber: toStringOrUndefined(document.documentLineNumber),
      documentType: document.documentType,
      referenceNumber: document.referenceNumber,
      sequenceNumber: document.sequenceNumber,
      type: CreateDocumentRequestV3TypeEnum.SUPPORTING,
      transitOperationId,
      consignmentItemId,
    },
    files: document.files,
  }
}

export function toUpdateConsignmentItemSupportingDocumentRequest(
  document: SupportingDocumentUpdate,
  transitOperationId: number,
  consignmentItemId: number | null,
): UpdateDocumentRequestWithFiles {
  return {
    request: {
      id: document.id,
      complementOfInformation: document.complementOfInformation,
      documentLineItemNumber: toStringOrUndefined(document.documentLineNumber),
      documentType: document.documentType,
      referenceNumber: document.referenceNumber,
      sequenceNumber: document.sequenceNumber,
      type: UpdateDocumentRequestV3TypeEnum.SUPPORTING,
      transitOperationId,
      consignmentItemId,
    },
    files: document.files,
  }
}

export function toCreateConsignmentItemPreviousDocumentRequest(
  document: PreviousDocumentCreate,
  transitOperationId: number,
  consignmentItemId: number | null,
): CreateDocumentRequestWithFiles {
  return {
    request: {
      sequenceNumber: document.sequenceNumber,
      type: CreateDocumentRequestV3TypeEnum.PREVIOUS,
      referenceNumber: document.referenceNumber,
      complementOfInformation: document.complementOfInformation,
      transportDocumentReferenceNumber: document.transportDocumentReferenceNumber,
      documentType: document.documentType,
      transitOperationId,
      consignmentItemId,
      procedureType: document.procedureType ?? undefined,
    },
    files: document.files,
  }
}

export function toUpdateConsignmentItemPreviousDocumentRequest(
  document: PreviousDocumentUpdate,
  transitOperationId: number,
  consignmentItemId: number | null,
): UpdateDocumentRequestWithFiles {
  return {
    request: {
      id: document.id,
      sequenceNumber: document.sequenceNumber,
      type: UpdateDocumentRequestV3TypeEnum.PREVIOUS,
      referenceNumber: document.referenceNumber,
      complementOfInformation: document.complementOfInformation,
      transportDocumentReferenceNumber: document.transportDocumentReferenceNumber,
      documentType: document.documentType,
      transitOperationId,
      consignmentItemId,
      procedureType: document.procedureType ?? undefined,
    },
    files: document.files,
  }
}

export function toCreateConsignmentItemTransportDocumentRequest(
  document: TransportDocumentCreate,
  transitOperationId: number,
  consignmentItemId: number | null,
): CreateDocumentRequestWithFiles {
  return {
    request: {
      sequenceNumber: document.sequenceNumber,
      type: CreateDocumentRequestV3TypeEnum.TRANSPORT,
      referenceNumber: document.referenceNumber,
      documentType: document.documentType,
      transitOperationId,
      consignmentItemId,
    },
    files: document.files,
  }
}

export function toUpdateConsignmentItemTransportDocumentRequest(
  document: TransportDocumentUpdate,
  transitOperationId: number,
  consignmentItemId: number | null,
): UpdateDocumentRequestWithFiles {
  return {
    request: {
      id: document.id,
      sequenceNumber: document.sequenceNumber,
      type: UpdateDocumentRequestV3TypeEnum.TRANSPORT,
      referenceNumber: document.referenceNumber,
      documentType: document.documentType,
      transitOperationId,
      consignmentItemId,
    },
    files: document.files,
  }
}

export function toCreateDocumentRequests(
  documents: DocumentCreate[],
  transitOperationId: number,
  consignmentItemId: number | null,
): CreateDocumentRequestV3[] {
  const previousDocumentRequests = documents.filter(isPreviousDocumentCreate)
    .map((d) => toCreateConsignmentItemPreviousDocumentRequest(d, transitOperationId, consignmentItemId))
    .map((requestWithFiles) => requestWithFiles.request)

  const supportingDocumentRequests = documents.filter(isSupportingDocumentCreate)
    .map((d) => toCreateConsignmentItemSupportingDocumentRequest(d, transitOperationId, consignmentItemId))
    .map((requestWithFiles) => requestWithFiles.request)

  const transportDocumentRequests = documents.filter(isTransportDocumentCreate)
    .map((d) => toCreateConsignmentItemTransportDocumentRequest(d, transitOperationId, consignmentItemId))
    .map((requestWithFiles) => requestWithFiles.request)

  return [
    ...previousDocumentRequests,
    ...supportingDocumentRequests,
    ...transportDocumentRequests,
  ]
}

export function toUpdateDocumentRequests(
  documents: DocumentUpdate[],
  transitOperationId: number,
  consignmentItemId: number | null,
): UpdateDocumentRequestV3[] {
  const previousDocumentRequests = documents.filter(isPreviousDocumentUpdate)
    .map((d) => toUpdateConsignmentItemPreviousDocumentRequest(d, transitOperationId, consignmentItemId))
    .map((requestWithFiles) => requestWithFiles.request)

  const supportingDocumentRequests = documents.filter(isSupportingDocumentUpdate)
    .map((d) => toUpdateConsignmentItemSupportingDocumentRequest(d, transitOperationId, consignmentItemId))
    .map((requestWithFiles) => requestWithFiles.request)

  const transportDocumentRequests = documents.filter(isTransportDocumentUpdate)
    .map((d) => toUpdateConsignmentItemTransportDocumentRequest(d, transitOperationId, consignmentItemId))
    .map((requestWithFiles) => requestWithFiles.request)

  return [
    ...previousDocumentRequests,
    ...supportingDocumentRequests,
    ...transportDocumentRequests,
  ]
}
